import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   List,
   ListItem,
   ListItemText,
   ListItemIcon,
   Typography,
   Box,
   Tabs,
   Tab,
   CircularProgress,
   Alert,
   Chip,
   Avatar,
} from '@mui/material';
import {
   Event as EventIcon,
   Flight as TripIcon,
   Group as GroupIcon,
   CalendarToday as CalendarIcon,
   LocationOn as LocationIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import webSocketService, { ChatRoom } from '../../../services/WebSocketService';
import { toast } from 'react-toastify';

interface CreateGroupChatDialogProps {
   open: boolean;
   onClose: () => void;
   onGroupChatCreated: (createdChannel: ChatRoom) => void;
}

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;
   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`group-chat-tabpanel-${index}`}
         aria-labelledby={`group-chat-tab-${index}`}
         {...other}
      >
         {value === index && <Box>{children}</Box>}
      </div>
   );
}

const CreateGroupChatDialog: React.FC<CreateGroupChatDialogProps> = ({
   open,
   onClose,
   onGroupChatCreated,
}) => {
   const [tabValue, setTabValue] = useState(0);
   const [events, setEvents] = useState<any[]>([]);
   const [trips, setTrips] = useState<any[]>([]);
   const [loading, setLoading] = useState(false);
   const [creating, setCreating] = useState(false);
   const [error, setError] = useState<string | null>(null);

   // Fetch events and trips when dialog opens
   useEffect(() => {
      if (open) {
         fetchEventsAndTrips();
      }
   }, [open]);

   const fetchEventsAndTrips = async () => {
      setLoading(true);
      setError(null);
      try {
         const { events: fetchedEvents, trips: fetchedTrips } =
            await webSocketService.getAvailableEventsAndTrips();
         setEvents(fetchedEvents);
         setTrips(fetchedTrips);
      } catch (err) {
         console.error('Error fetching events and trips:', err);
         setError('Failed to load events and trips');
      } finally {
         setLoading(false);
      }
   };

   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   const handleCreateEventGroupChat = async (eventId: string, eventTitle: string) => {
      setCreating(true);
      try {
         console.log('Creating event group chat for:', eventTitle);

         const createdRoom = await webSocketService.createEventGroupChat(eventId);

         console.log('✅ Event group chat created:', createdRoom.name);
         toast.success(`Event group chat "${eventTitle}" created successfully!`);

         // Pass the full ChatRoom object
         onGroupChatCreated(createdRoom);
         onClose();
      } catch (error: any) {
         console.error('❌ Error creating event group chat:', error);
         toast.error(error.message || 'Failed to create event group chat');
         setError(error.message || 'Failed to create group chat');
      } finally {
         setCreating(false);
      }
   };

   const handleCreateTripGroupChat = async (tripId: string, tripTitle: string) => {
      setCreating(true);
      try {
         console.log('Creating trip group chat for:', tripTitle);

         const createdRoom = await webSocketService.createTripGroupChat(tripId, tripTitle);

         console.log('✅ Trip group chat created:', createdRoom.name);
         toast.success(`Trip group chat "${tripTitle}" created successfully!`);

         // Pass the full ChatRoom object
         onGroupChatCreated(createdRoom);
         onClose();
      } catch (error: any) {
         console.error('❌ Error creating trip group chat:', error);
         toast.error(error.message || 'Failed to create trip group chat');
         setError(error.message || 'Failed to create group chat');
      } finally {
         setCreating(false);
      }
   };

   const formatDate = (date: any) => {
      if (!date) return 'No date';
      try {
         const dateObj = date.toDate ? date.toDate() : new Date(date);
         return format(dateObj, 'MMM dd, yyyy');
      } catch (err) {
         return 'Invalid date';
      }
   };

   // Helper function to safely extract location string
   const formatLocation = (location: any) => {
      if (!location) return null;

      // If location is a string, return it
      if (typeof location === 'string') {
         return location;
      }

      // If location is an object, try to extract name, address, or other string fields
      if (typeof location === 'object') {
         return (
            location.name ||
            location.address ||
            location.city ||
            location.label ||
            'Unknown Location'
         );
      }

      return 'Location';
   };

   // Helper function to safely extract destination string
   const formatDestination = (destination: any) => {
      if (!destination) return null;

      // If destination is a string, return it
      if (typeof destination === 'string') {
         return destination;
      }

      // If destination is an object, try to extract name, address, or other string fields
      if (typeof destination === 'object') {
         return (
            destination.name ||
            destination.address ||
            destination.city ||
            destination.label ||
            'Unknown Destination'
         );
      }

      return 'Destination';
   };

   const handleClose = () => {
      if (!creating) {
         onClose();
      }
   };

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth='md'
         fullWidth
         PaperProps={{
            sx: { minHeight: '500px' },
         }}
      >
         <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <GroupIcon color='primary' />
               <Typography variant='h6'>Create Group Chat</Typography>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
               Select an event or trip to create a group chat for all participants
            </Typography>
         </DialogTitle>

         <DialogContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
               <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab
                     label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <EventIcon />
                           Events ({events.length})
                        </Box>
                     }
                  />
                  <Tab
                     label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <TripIcon />
                           Trips ({trips.length})
                        </Box>
                     }
                  />
               </Tabs>
            </Box>

            {loading && (
               <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
               </Box>
            )}

            {error && (
               <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
               </Alert>
            )}

            {!loading && !error && (
               <>
                  <TabPanel value={tabValue} index={0}>
                     {events.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                           <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                           <Typography color='text.secondary'>
                              No events found. Create or join events to start group chats.
                           </Typography>
                        </Box>
                     ) : (
                        <List>
                           {events.map((event) => (
                              <ListItem
                                 key={event.id}
                                 button
                                 onClick={() => handleCreateEventGroupChat(event.id, event.title)}
                                 disabled={creating}
                                 sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    '&:hover': {
                                       backgroundColor: 'action.hover',
                                    },
                                 }}
                              >
                                 <ListItemIcon>
                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                       <EventIcon />
                                    </Avatar>
                                 </ListItemIcon>
                                 <ListItemText
                                    primary={
                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant='subtitle1' fontWeight='medium'>
                                             {event.title}
                                          </Typography>
                                          {event.organizationId && (
                                             <Chip
                                                label='Organization'
                                                size='small'
                                                color='primary'
                                             />
                                          )}
                                       </Box>
                                    }
                                    secondary={
                                       <Box sx={{ mt: 0.5 }}>
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                mb: 0.5,
                                             }}
                                          >
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   gap: 0.5,
                                                }}
                                             >
                                                <CalendarIcon
                                                   sx={{ fontSize: 16, color: 'text.secondary' }}
                                                />
                                                <Typography variant='body2' color='text.secondary'>
                                                   {formatDate(event.startDate)}
                                                </Typography>
                                             </Box>
                                             {event.location && (
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                   }}
                                                >
                                                   <LocationIcon
                                                      sx={{ fontSize: 16, color: 'text.secondary' }}
                                                   />
                                                   <Typography
                                                      variant='body2'
                                                      color='text.secondary'
                                                   >
                                                      {formatLocation(event.location)}
                                                   </Typography>
                                                </Box>
                                             )}
                                          </Box>
                                          <Typography variant='body2' color='text.secondary'>
                                             {event.participants?.length || 0} participants
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              </ListItem>
                           ))}
                        </List>
                     )}
                  </TabPanel>

                  <TabPanel value={tabValue} index={1}>
                     {trips.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                           <TripIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                           <Typography color='text.secondary'>
                              No trips found. Create or join trips to start group chats.
                           </Typography>
                        </Box>
                     ) : (
                        <List>
                           {trips.map((trip) => (
                              <ListItem
                                 key={trip.id}
                                 button
                                 onClick={() =>
                                    handleCreateTripGroupChat(
                                       trip.id,
                                       trip.title || formatDestination(trip.destination) || 'Trip'
                                    )
                                 }
                                 disabled={creating}
                                 sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    '&:hover': {
                                       backgroundColor: 'action.hover',
                                    },
                                 }}
                              >
                                 <ListItemIcon>
                                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                       <TripIcon />
                                    </Avatar>
                                 </ListItemIcon>
                                 <ListItemText
                                    primary={
                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant='subtitle1' fontWeight='medium'>
                                             {trip.title ||
                                                formatDestination(trip.destination) ||
                                                'Trip'}
                                          </Typography>
                                          {trip.organizationId && (
                                             <Chip
                                                label='Organization'
                                                size='small'
                                                color='secondary'
                                             />
                                          )}
                                       </Box>
                                    }
                                    secondary={
                                       <Box sx={{ mt: 0.5 }}>
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                mb: 0.5,
                                             }}
                                          >
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   gap: 0.5,
                                                }}
                                             >
                                                <CalendarIcon
                                                   sx={{ fontSize: 16, color: 'text.secondary' }}
                                                />
                                                <Typography variant='body2' color='text.secondary'>
                                                   {formatDate(trip.startDate)}
                                                </Typography>
                                             </Box>
                                             {trip.destination && (
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                   }}
                                                >
                                                   <LocationIcon
                                                      sx={{ fontSize: 16, color: 'text.secondary' }}
                                                   />
                                                   <Typography
                                                      variant='body2'
                                                      color='text.secondary'
                                                   >
                                                      {formatDestination(trip.destination)}
                                                   </Typography>
                                                </Box>
                                             )}
                                          </Box>
                                          <Typography variant='body2' color='text.secondary'>
                                             {(trip.participants?.length || 0) +
                                                (trip.members?.length || 0)}{' '}
                                             participants
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              </ListItem>
                           ))}
                        </List>
                     )}
                  </TabPanel>
               </>
            )}
         </DialogContent>

         <DialogActions>
            <Button onClick={handleClose} disabled={creating}>
               Cancel
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default CreateGroupChatDialog;
